import React, {useEffect, useState} from 'react';
import {BarChartFill, BellFill, TrophyFill} from '@goproblems/ui/icons';
import {OverlayTrigger, Popover, PopoverHeader, FormCheck} from '@goproblems/ui';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faArrowRight} from '@fortawesome/free-solid-svg-icons';
import {useTranslation} from 'react-i18next';
import {calculateEloLevelName, convertEloToChartLevel, getShortLevelName} from '@utils/elo';
import {useAuthenticated, useSelector} from '@utils/hooks/common';
import {useUserPreferences} from '@utils/hooks/useUserPreferences';
import {Loading} from '@components/Loading';
import {NothingHere} from '@components/NothingHere';
import {RatingDynamicChart} from '@components/RatingDynamicChart';
import {QuestionMarkPopover} from '@components/QuestionMarkPopover';
import {
    useLazyFetchNotificationsQuery,
    useLazyFetchUserRatingDynamicQuery,
    useMarkAsViewedMutation,
    useUpdateUserPreferencesMutation,
    useFetchStatusCountsQuery,
} from '@api';
import {NotificationItem} from '@components/notifications/NotificationItem';
import {useTheme} from '@components/theme/useTheme';
import {Link} from 'react-router';
import {MetricEntryAnon} from '@types';
import {
    Chart,
    LineElement,
    LineController,
    LinearScale,
    CategoryScale,
    Tooltip,
    registerables,
} from 'chart.js';
import {Line} from 'react-chartjs-2';
import {ELO_LEVEL_BASE} from '@utils/const/level';
import {getAnonymousEloHistory} from '@utils/anonymousProblemSolve';

Chart.register(
    LineController,
    LineElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    ...registerables
);

const RatingDynamicChartAnon = ({
    ratingData,
    preview,
}: {
    ratingData: MetricEntryAnon;
    preview?: boolean;
}) => {
    const {t} = useTranslation();
    const data = {
        labels: ratingData.map((_, index) => index),
        datasets: [
            {
                label: t('common.rating_history'),
                data: ratingData.map(convertEloToChartLevel),
            },
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: preview,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    label: (context: {dataIndex: number}) =>
                        `${t('common.rating')}: ${calculateEloLevelName(ratingData[context.dataIndex], true)}`,
                },
            },
        },
        scales: {
            x: {
                display: !preview,
            },
            y: {
                ticks: {
                    stepSize: 1,
                    callback: (value: string | number) =>
                        getShortLevelName(value as number),
                },
            },
        },
    };

    return <Line id="ratingDynamic" data={data} options={options} />;
};

function AnonRating() {
    const {t} = useTranslation();
    const ratingRefreshCounter = useSelector(
        state => state.ui.userRatingRefreshCount
    );
    const [ratingDataAnon, setRatingDataAnon] = useState<number[]>(
        getAnonymousEloHistory()
    );

    const [eloLevel, setEloLevel] = useState<string | undefined>(
        calculateEloLevelName(ELO_LEVEL_BASE, true)
    );

    useEffect(() => {
        setRatingDataAnon(getAnonymousEloHistory());
    }, [ratingRefreshCounter]);

    useEffect(() => {
        if (ratingDataAnon && ratingDataAnon.length > 0) {
            setEloLevel(
                calculateEloLevelName(
                    ratingDataAnon[ratingDataAnon.length - 1],
                    true
                )
            );
            return;
        }

        setEloLevel(calculateEloLevelName(ELO_LEVEL_BASE, true));
    }, [ratingDataAnon]);

    const ratingPopoverAnon = (
        <Popover id="rating-popover" className="rating-popover">
            <PopoverHeader>{t('common.rating_history')}</PopoverHeader>
            <div className="flex flex-col p-3 items-center">
                {ratingDataAnon && (
                    <>
                        <RatingDynamicChartAnon
                            ratingData={ratingDataAnon}
                            preview={true}
                        />
                    </>
                )}
            </div>
        </Popover>
    );

    return (
        <>
            <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={ratingPopoverAnon}
                onEnter={() => {}}>
                <a
                    className="flex items-center gap-1 pr-3 px-md-2 px-lg-4 fw-bold status-default text-decoration-none !text-neutral-500 dark:!text-neutral-300"
                    tabIndex={0}
                    role="button">
                    <BarChartFill />

                    {eloLevel}
                </a>
            </OverlayTrigger>
        </>
    );
}

export const StatusBar = () => {
    const theme = useTheme();
    const {user} = useAuthenticated();
    const {t} = useTranslation();
    const {statusBarUserElo} = useSelector(state => state.ui);
    const eloLevel =
        statusBarUserElo !== null
            ? calculateEloLevelName(statusBarUserElo, true)
            : user?.eloLevel;
    const [isRatedMode, setIsRatedMode] = useState(
        user?.preferences?.ratedMode ?? true
    );

    const [
        fetchUserRatingDynamic,
        {data: ratingData, isLoading: isRatingLoading},
    ] = useLazyFetchUserRatingDynamicQuery();

    const [
        fetchNotifications,
        {data: notificationsData, isLoading: isNotificationsLoading},
    ] = useLazyFetchNotificationsQuery();

    const {data: statusCounts, refetch: refetchStatusCounts} =
        useFetchStatusCountsQuery(undefined, {
            skip: !user,
        });

    const [markAsViewed] = useMarkAsViewedMutation();

    const {preferences} = useUserPreferences();

    const [updateUserPreferences] = useUpdateUserPreferencesMutation();

    useEffect(() => {
        if (preferences) {
            setIsRatedMode(preferences.ratedMode);
        }
    }, [preferences]);

    const handleRatedModeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.checked;
        setIsRatedMode(newValue);
        if (user?.id) {
            void updateUserPreferences({
                ratedMode: newValue,
            });
        }
    };

    if (!user)
        return (
            <>
                <AnonRating />
            </>
        );

    const ratingPopover = (
        <Popover id="rating-popover" className="rating-popover">
            <div className="flex flex-col">
                <div className="px-2 pt-2 pb-1 border-b border-b-neutral-300">
                    <div className="flex items-center">
                        <FormCheck
                            className="!mb-0"
                            type="checkbox"
                            id="rated-mode-checkbox"
                            label={t('navbar.rated_mode')}
                            checked={isRatedMode}
                            onChange={handleRatedModeChange}
                        />
                        <QuestionMarkPopover
                            title={t('navbar.rated_mode')}
                            content={t('navbar.rated_mode_description')}
                            className="leading-none"
                            trigger="hover"
                        />
                    </div>
                </div>
                <PopoverHeader className="rounded-none">
                    {t('navbar.rating_history')}
                </PopoverHeader>
                <div className="flex flex-col p-3 items-center">
                    {isRatingLoading && <Loading />}
                    {ratingData && (
                        <>
                            <RatingDynamicChart
                                ratingData={ratingData}
                                preview={true}
                            />
                            <Link
                                to="/dashboard"
                                className="no-underline link self-start text-body mt-2">
                                {t('navbar.see_details')} {'>'}
                            </Link>
                        </>
                    )}
                </div>
            </div>
        </Popover>
    );

    const notificationsPopover = (
        <Popover
            id="notifications-popover"
            className="notifications-popover"
            title={t('common.notifications')}>
            <PopoverHeader>{t('common.notifications')}</PopoverHeader>
            <div className="flex flex-col p-2 items-center">
                {isNotificationsLoading && <Loading />}
                {notificationsData && notificationsData.length === 0 && (
                    <NothingHere />
                )}
                {notificationsData && user && (
                    <div className="list-group list-group-flush">
                        {notificationsData.map(n => (
                            <NotificationItem
                                key={n.id}
                                notification={n}
                                username={user?.name}
                            />
                        ))}
                    </div>
                )}
                <div className="w-100 mt-2 pt-2 border-top text-end">
                    <Link
                        to="/notifications"
                        className="no-underline link text-body align-middle">
                        {t('common.see_all')}{' '}
                        <FontAwesomeIcon className="ml-1" icon={faArrowRight} />
                    </Link>
                </div>
            </div>
        </Popover>
    );

    return (
        <div
            className={`w-100 d-flex justify-content-center${theme === 'dark' ? ' theme-dark' : ''}`}>
            <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={ratingPopover}
                onEnter={() => fetchUserRatingDynamic({gap: 'month'})}>
                <a
                    className="flex items-center gap-1 pr-3 px-md-2 px-lg-4 fw-bold status-default text-decoration-none !text-neutral-500 dark:!text-neutral-300"
                    tabIndex={0}
                    role="button">
                    <BarChartFill />

                    <span className="gh-status-rank">{eloLevel}</span>
                </a>
            </OverlayTrigger>
            <Link
                to={`/users/${user?.name}/achievements`}
                className="flex items-center gap-1 px-3 px-md-2 px-lg-4 fw-bold status-default text-decoration-none !text-neutral-500 dark:!text-neutral-300">
                <TrophyFill />
                <span className="gh-status-count">
                    {statusCounts?.achievementsCount ?? null}
                </span>
            </Link>
            <OverlayTrigger
                trigger="click"
                rootClose
                placement="bottom"
                overlay={notificationsPopover}
                onEnter={async () => {
                    try {
                        await fetchNotifications(false);
                        await markAsViewed();
                        await refetchStatusCounts();
                    } catch (error) {
                        console.log(error);
                    }
                }}
                onExited={() => fetchNotifications(false)}>
                <a
                    className="flex items-center gap-1 px-3 px-md-2 px-lg-4 fw-bold status-default text-decoration-none !text-neutral-500 dark:!text-neutral-300"
                    tabIndex={0}
                    role="button"
                    data-bs-toggle="popover"
                    data-bs-trigger="focus"
                    title={t('common.notifications')}>
                    <BellFill />
                    <span className="gh-status-count">
                        {statusCounts?.unreadNotificationCount ?? null}
                    </span>
                </a>
            </OverlayTrigger>
        </div>
    );
};
